﻿import { useEffect, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FinancialYearProvider } from "@/contexts/FinancialYearContext";
import { FinancialYearChangeOverlay } from "@esgcaffe/ui-common";
import { UserflowProvider } from "@/contexts/UserflowContext";
import { ChartColorsProvider } from "@/contexts/ChartColorsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import DynamicRoute from "@/components/DynamicRoute";
import { preloadAllComponents } from "@/config/routeComponents";

// Auth Pages (not lazy loaded for better UX)
import Login from "./pages/Login";
import LoginVerifyOtp from "./pages/LoginVerifyOtp";
import SubscriptionExpired from "./pages/SubscriptionExpired";
import AcceptInvitation from "./pages/AcceptInvitation";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Website CSS (needed for all website pages)
import "./websites/src/App.css";
import "./websites/src/index.css";

// Website layout & homepage (eagerly loaded)
import { ThemeProvider } from "./websites/src/components/layout/ThemeContext";
import PageLayout from "./websites/src/components/layout/PageLayout";
import ESGMain from "./websites/src/pages/ESGMain";

// Lazy-load website sub-pages for code splitting
const FeaturesPage = lazy(() => import("./websites/src/pages/FeaturesPage"));
const AIPlatformPage = lazy(() => import("./websites/src/pages/AIPlatformPage"));
const FrameworksPage = lazy(() => import("./websites/src/pages/FrameworksPage"));
const PricingPage = lazy(() => import("./websites/src/pages/PricingPage"));
const FAQPage = lazy(() => import("./websites/src/pages/FAQPage"));
const HowItWorksPage = lazy(() => import("./websites/src/pages/HowItWorksPage"));
const IntegrationsPage = lazy(() => import("./websites/src/pages/IntegrationsPage"));
const SecurityPage = lazy(() => import("./websites/src/pages/SecurityPage"));
const ComparisonPage = lazy(() => import("./websites/src/pages/ComparisonPage"));
const AboutPage = lazy(() => import("./websites/src/pages/AboutPage"));
const ContactPage = lazy(() => import("./websites/src/pages/ContactPage"));
const BlogPage = lazy(() => import("./websites/src/pages/BlogPage"));
const CaseStudiesPage = lazy(() => import("./websites/src/pages/CaseStudiesPage"));
const DocsPage = lazy(() => import("./websites/src/pages/DocsPage"));
const APIPage = lazy(() => import("./websites/src/pages/APIPage"));
const FrameworkDetailPage = lazy(() => import("./websites/src/pages/FrameworkDetailPage"));
const ManufacturingPage = lazy(() => import("./websites/src/pages/ManufacturingPage"));
const ConsultingPage = lazy(() => import("./websites/src/pages/ConsultingPage"));
const FinancialServicesPage = lazy(() => import("./websites/src/pages/FinancialServicesPage"));
const RealEstatePage = lazy(() => import("./websites/src/pages/RealEstatePage"));
const HealthcarePage = lazy(() => import("./websites/src/pages/HealthcarePage"));
const PartnershipsPage = lazy(() => import("./websites/src/pages/PartnershipsPage"));
const AnnouncementsPage = lazy(() => import("./websites/src/pages/AnnouncementsPage"));

// Public pages (lazy loaded)
const StakeholderSurvey = lazy(() => import('./pages/StakeholderSurvey'));
const SupplierSurvey = lazy(() => import('./pages/SupplierSurvey'));
const CbamSupplierForm = lazy(() => import('./pages/CbamSupplierForm'));
const PublicPcfRequest = lazy(() => import('./pages/PublicPcfRequest'));
const GuestDataForm = lazy(() => import('./pages/GuestDataForm'));
const CctsPlanReview = lazy(() => import('./pages/CctsPlanReview'));
const DmrvAttestation = lazy(() => import('./pages/DmrvAttestation'));
const CctsEvidencePack = lazy(() => import('./pages/CctsEvidencePack'));
const VerifierView = lazy(() => import('./pages/VerifierView'));
const PartnerPcfRequest = lazy(() => import('./pages/PartnerPcfRequest'));
const SupplierPcfResponse = lazy(() => import('./pages/SupplierPcfResponse'));


const queryClient = new QueryClient();

/**
 * The shell locks html/body overflow globally in index.css so the authenticated product
 * can manage its own inner scroll containers. Every route OUTSIDE that product shell —
 * auth pages, public surveys, marketing, invitation acceptance — needs normal window scroll.
 *
 * PRODUCT_PATH_PREFIXES lists the authenticated-app URL prefixes. If the current path
 * matches none of them, we add `.website-page` to <body>, which the CSS uses to restore
 * scroll (see index.css `body.website-page` rules).
 */
const PRODUCT_PATH_PREFIXES = [
  '/dashboard', '/settings', '/pcf', '/assurance', '/green_certification',
  '/communication', '/esg-data', '/reporting', '/analytics', '/compliance',
  '/sustainability', '/carbon', '/supplier', '/lca', '/chat',
];

const WindowScrollController = () => {
  const location = useLocation();
  useEffect(() => {
    const isProduct = PRODUCT_PATH_PREFIXES.some(p =>
      location.pathname === p || location.pathname.startsWith(p + '/')
    );
    if (isProduct) {
      document.body.classList.remove('website-page');
    } else {
      document.body.classList.add('website-page');
    }
  }, [location.pathname]);
  return null;
};


const App = () => {
  useEffect(() => {
    preloadAllComponents();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ChartColorsProvider>
      <FinancialYearProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <FinancialYearChangeOverlay />
          <BrowserRouter>
            <WindowScrollController />
            <Routes>
              {/* Public Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/login/verify-otp" element={<LoginVerifyOtp />} />
              <Route path="/subscription-expired" element={<SubscriptionExpired />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Public Website Routes — wrapped in ThemeProvider + PageLayout (Navbar + Footer) */}
              <Route element={
                <ThemeProvider>
                  <PageLayout />
                </ThemeProvider>
              }>
                <Route path="/" element={<Navigate to="/esg-360" replace />} />
                <Route path="/esg-360" element={<ESGMain />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/ai-platform" element={<AIPlatformPage />} />
                <Route path="/frameworks" element={<FrameworksPage />} />
                <Route path="/frameworks/:slug" element={<FrameworkDetailPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/comparison" element={<ComparisonPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/case-studies" element={<CaseStudiesPage />} />
                <Route path="/docs" element={<DocsPage />} />
                <Route path="/api" element={<APIPage />} />
                <Route path="/industries" element={<Navigate to="/industries/manufacturing" replace />} />
                <Route path="/industries/manufacturing" element={<ManufacturingPage />} />
                <Route path="/industries/consulting" element={<ConsultingPage />} />
                <Route path="/industries/financial-services" element={<FinancialServicesPage />} />
                <Route path="/industries/real-estate" element={<RealEstatePage />} />
                <Route path="/industries/healthcare" element={<HealthcarePage />} />
                <Route path="/partnerships" element={<PartnershipsPage />} />
                <Route path="/announcements" element={<AnnouncementsPage />} />
              </Route>

              {/* Public Survey Route — no login required */}
              <Route path="/survey/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}><StakeholderSurvey /></Suspense>} />

              {/* Public Supplier Survey Route — no login required.
                  Backed by services/supplier /api/supplier/public/:token. */}
              <Route path="/supplier-survey/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}><SupplierSurvey /></Suspense>} />
              {/* Public CBAM supplier emissions form — token-scoped, no login. Backed by services/cbam /api/cbam/public/supplier-form/:token.
                  Paired with apps/cbam-mfe/src/pages/SupplierFormPage.tsx (identical file). */}
              <Route path="/cbam-supplier/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><CbamSupplierForm /></Suspense>} />
              <Route path="/pcf-request/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><PublicPcfRequest /></Suspense>} />
              <Route path="/guest/data/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><GuestDataForm /></Suspense>} />
              {/* Public, token-scoped: farmer / end user confirms biochar use (Puro 3.6.5.b.iii). Backed by services/dmrv /api/dmrv/public/attest/:token. */}
              <Route path="/attest/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><DmrvAttestation /></Suspense>} />
              {/* Public, token-scoped: external ACV verifier responds to a CCTS monitoring plan without an account. */}
              <Route path="/ccts/plan-review/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><CctsPlanReview /></Suspense>} />
              <Route path="/ccts/evidence-pack/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><CctsEvidencePack /></Suspense>} />
              <Route path="/verifier/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}><VerifierView /></Suspense>} />
              <Route path="/pcf-partner-request/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}><PartnerPcfRequest /></Suspense>} />
              <Route path="/pcf-supplier-response/:token" element={<Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}><SupplierPcfResponse /></Suspense>} />

              {/* Protected Dynamic Routes — handles all authenticated app paths */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <UserflowProvider>
                      <DynamicRoute />
                    </UserflowProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </FinancialYearProvider>
      </ChartColorsProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
